import {
  EmptyState,
  formatDate,
  LoadingState,
  PageLayout,
  SectionCard,
  Spinner,
} from '@repo/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  CircleIcon,
  RefreshCwIcon,
  Trash2Icon,
  XCircleIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { ToneBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { fetchOrganizationDeletion, retryPrivacyOperation } from '@/lib/api'
import type {
  PrivacyOperation,
  PrivacyOperationStatus,
  PrivacyOperationStep,
} from '@/lib/api-types'

/**
 * Acompanhamento da exclusão de um tenant. Rota irmã de `$orgId` (não
 * aninhada): a organização deixa de existir no meio do processo e a página
 * de detalhe some, mas a trilha da operação continua consultável.
 */
export const Route = createFileRoute('/_app/organizacoes/$orgId_/exclusao')({
  component: OrganizationDeletionPage,
})

const STATUS_LABEL: Record<PrivacyOperationStatus, string> = {
  PENDING: 'Na fila',
  RUNNING: 'Em execução',
  COMPLETED: 'Concluída',
  COMPLETED_WITH_WARNINGS: 'Concluída com pendências',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelada',
}

const STATUS_TONE: Record<
  PrivacyOperationStatus,
  'success' | 'info' | 'warning' | 'destructive' | 'neutral'
> = {
  PENDING: 'info',
  RUNNING: 'info',
  COMPLETED: 'success',
  COMPLETED_WITH_WARNINGS: 'warning',
  FAILED: 'destructive',
  CANCELLED: 'neutral',
}

const STEP_LABEL: Record<string, string> = {
  DISCOVER: 'Inventário',
  FREEZE: 'Congelar automações',
  OFFBOARD_CHANNELS: 'Offboarding dos canais',
  EXTERNAL: 'Integrações externas',
  MEDIA: 'Limpar armazenamento (R2)',
  PURGE: 'Apagar no banco',
  VERIFY: 'Verificar',
}

const CATEGORY_LABEL: Record<string, string> = {
  organization: 'Organização',
  contacts: 'Contatos',
  conversations: 'Atendimentos',
  messages: 'Mensagens',
  channels: 'Canais',
  members: 'Membros',
  integrations: 'Integrações',
  webhookEndpoints: 'Webhooks de saída',
  inboundWebhookEndpoints: 'Webhooks de entrada',
  mediaAssets: 'Mídias na biblioteca',
  storageObjects: 'Objetos no armazenamento',
  campaigns: 'Campanhas',
  cadences: 'Cadências',
  appointments: 'Agendamentos',
  calendarIntegrations: 'Integrações de calendário',
}

const ACTIVE = new Set<PrivacyOperationStatus>(['PENDING', 'RUNNING'])

function StepIcon({ status }: { status: PrivacyOperationStep['status'] }) {
  if (status === 'COMPLETED')
    return <CheckCircle2Icon className="text-success size-4" />
  if (status === 'FAILED')
    return <XCircleIcon className="text-destructive size-4" />
  if (status === 'RUNNING') return <Spinner className="text-muted-foreground" />
  return <CircleIcon className="text-muted-foreground/50 size-4" />
}

function OrganizationDeletionPage() {
  const { orgId } = Route.useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const operation = useQuery({
    queryKey: ['organization-deletion', orgId],
    queryFn: () => fetchOrganizationDeletion(orgId),
    refetchInterval: (query) =>
      query.state.data && ACTIVE.has(query.state.data.status) ? 3000 : false,
  })
  const retry = useMutation({
    mutationFn: (input: { id: string; skipExternal?: boolean }) =>
      retryPrivacyOperation(input.id, { skipExternal: input.skipExternal }),
    onSuccess: async () => {
      toast.success('Operação reexecutada')
      await qc.invalidateQueries({ queryKey: ['organization-deletion', orgId] })
    },
    onError: () => toast.error('Falha ao reexecutar a operação'),
  })

  const data: PrivacyOperation | null | undefined = operation.data

  return (
    <PageLayout
      title={data ? `Exclusão de ${data.subjectLabel ?? orgId}` : 'Exclusão'}
      description="Offboarding completo do tenant: canais, integrações, armazenamento e banco, com verificação final. A trilha permanece depois que a organização deixa de existir."
      back={
        <Button
          variant="ghost"
          size="icon"
          title="Voltar para Organizações"
          onClick={() => void navigate({ to: '/organizacoes' })}
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
      }
      meta={
        data ? (
          <ToneBadge
            label={STATUS_LABEL[data.status]}
            tone={STATUS_TONE[data.status]}
          />
        ) : null
      }
    >
      {operation.isLoading ? (
        <LoadingState />
      ) : !data ? (
        <EmptyState
          variant="inline"
          className="min-h-80"
          icon={Trash2Icon}
          title="Nenhuma exclusão registrada"
          description="A exclusão é iniciada na aba Acesso do detalhe da organização."
        />
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <SectionCard
              title="Operação"
              description={
                <>
                  Aberta em {formatDate(data.createdAt)}
                  {data.completedAt
                    ? ` · concluída em ${formatDate(data.completedAt)}`
                    : ''}
                </>
              }
              actions={
                data.status === 'FAILED' ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate({ id: data.id })}
                    >
                      <RefreshCwIcon className="size-4" />
                      Reexecutar
                    </Button>
                    {data.steps.some(
                      (step) =>
                        step.key === 'EXTERNAL' && step.status === 'FAILED'
                    ) ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={retry.isPending}
                        title="Pula a limpeza externa (você assume fazê-la manualmente); fica registrado como pendência."
                        onClick={() =>
                          retry.mutate({ id: data.id, skipExternal: true })
                        }
                      >
                        Pular integrações externas
                      </Button>
                    ) : null}
                  </>
                ) : null
              }
            >
              {data.lastErrorCode ? (
                <div className="border-destructive/30 bg-destructive-soft/60 text-destructive-soft-foreground rounded-lg border p-4 text-sm">
                  <p className="font-medium">{data.lastErrorCode}</p>
                  {data.lastErrorCode === 'BILLING_ACTIVE_SUBSCRIPTION' ? (
                    <p className="mt-1">
                      A organização tem assinatura Stripe ativa. Cancele-a na
                      Stripe (ou pela aba Assinatura) e reexecute.
                    </p>
                  ) : data.lastErrorMessage ? (
                    <p className="mt-1">{data.lastErrorMessage}</p>
                  ) : null}
                </div>
              ) : null}

              {data.reason ? (
                <p className="text-sm">
                  <span className="text-muted-foreground">Justificativa:</span>{' '}
                  {data.reason}
                </p>
              ) : null}
              {!data.lastErrorCode && !data.reason ? (
                <p className="text-muted-foreground text-sm">
                  Sem erros registrados.
                </p>
              ) : null}
            </SectionCard>

            <SectionCard
              title="Etapas"
              description="Cada etapa é idempotente e pode ser reexecutada."
            >
              <ol className="space-y-2">
                {data.steps.map((step) => {
                  const counts = Object.entries(step.counts ?? {}).filter(
                    ([, value]) => value > 0
                  )
                  return (
                    <li key={step.id} className="rounded-lg border p-3">
                      <div className="flex items-center gap-2">
                        <StepIcon status={step.status} />
                        <span className="text-sm font-medium">
                          {STEP_LABEL[step.key] ?? step.key}
                        </span>
                        <span className="text-muted-foreground ml-auto text-xs">
                          {step.completedAt
                            ? formatDate(step.completedAt)
                            : step.attempts > 0
                              ? `${step.attempts} tentativa(s)`
                              : ''}
                        </span>
                      </div>
                      {step.lastErrorCode ? (
                        <p className="text-destructive-soft-foreground mt-1 text-xs">
                          {step.lastErrorCode}
                          {step.lastErrorMessage
                            ? ` — ${step.lastErrorMessage}`
                            : ''}
                        </p>
                      ) : null}
                      {counts.length > 0 ? (
                        <ul className="text-muted-foreground mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                          {counts.map(([key, value]) => (
                            <li
                              key={key}
                              className="flex justify-between gap-2"
                            >
                              <span className="truncate">
                                {CATEGORY_LABEL[key] ?? key}
                              </span>
                              <span className="tabular-nums">{value}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  )
                })}
              </ol>
            </SectionCard>
          </div>

          <aside className="space-y-6">
            <SectionCard title="Pendências externas">
              {data.warnings && data.warnings.length > 0 ? (
                <ul className="space-y-2 text-xs">
                  {data.warnings.map((warning) => (
                    <li
                      key={warning.code}
                      className="text-warning-soft-foreground flex items-start gap-1.5"
                    >
                      <AlertTriangleIcon className="mt-0.5 size-3 shrink-0" />
                      <span>
                        {warning.message}
                        {warning.count ? ` (${warning.count})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Nenhuma pendência registrada.
                </p>
              )}
            </SectionCard>

            {data.inventoryBefore ? (
              <SectionCard
                title="Inventário"
                description="Registros antes e depois"
              >
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-normal">Categoria</th>
                      <th className="text-right font-normal">Antes</th>
                      <th className="text-right font-normal">Depois</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.inventoryBefore.categories)
                      .filter(
                        ([category, count]) =>
                          count > 0 ||
                          (data.inventoryAfter?.categories[category] ?? 0) > 0
                      )
                      .map(([category, count]) => (
                        <tr key={category}>
                          <td className="py-0.5">
                            {CATEGORY_LABEL[category] ?? category}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">
                            {count}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">
                            {data.inventoryAfter
                              ? (data.inventoryAfter.categories[category] ?? 0)
                              : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </SectionCard>
            ) : null}
          </aside>
        </div>
      )}
    </PageLayout>
  )
}
